import { useAtom } from "jotai";
import styles from "./StockheaderInfo.module.scss";
import { tickerTapeState } from "@/lib/store";
import { FC, useCallback, useContext, useEffect, useState } from "react";
import { Context } from "@/lib/Context";
import { Stock } from "@/models/stock";
import { classNames, formatPriceWithPrecision, priceDiffPercentage } from "@/lib/utils";
import StockHeaderSession from "@/components/stock-header-session/StockHeaderSession";

const GB_CURRENCIES = ["GBP", "GBX"];

const StockHeaderInfo: FC<{ stock: Stock }> = ({ stock }) => {
    const { ortexWebSocket } = useContext(Context);
    const [tickerStocks] = useAtom(tickerTapeState);
    const [isStockInTickerTape, setIsStockInTickerTape] = useState(false);

    const [price, setPrice] = useState(undefined);

    const isLiveFeed = stock?.livePriceFeed;

    const GBPIdentifier = `${stock?.isin}GBP`;
    const GBXIdentifier = `${stock?.isin}GBX`;

    const [isGBCurrency, setIsGBCurrency] = useState(GB_CURRENCIES.includes(stock?.currency));

    useEffect(() => {
        setIsGBCurrency(GB_CURRENCIES.includes(stock?.currency));
    }, [stock.currency]);

    const websocket = isLiveFeed === "NA" || isLiveFeed === "EU" ? ortexWebSocket : null;

    const tsMessage = isLiveFeed === "NA" ? { type: "last_trades_history_w_ticker", identifier: stock.ticker } : isLiveFeed === "EU" ? { type: "last_trades_history_w_ticker", identifier: `${stock.isin}${stock.currency}` } : null;
    const priceMessage = isLiveFeed === "NA" ? { type: "subscribe", identifier: stock.ticker } : isLiveFeed === "EU" ? { type: "subscribe", identifier: `${stock.isin}${stock.currency}` } : null;

    const hasValidPrice = isFinite(parseFloat(price)) && stock.last_close;

    const percentageDifference = hasValidPrice ? priceDiffPercentage(stock.last_close, parseFloat(price)) : NaN;
    const closeDifference = hasValidPrice ? Number(price) - stock.last_close : NaN;

    const isNegative = closeDifference < 0;
    const sign = isNegative ? "" : "+";

    const sendMessage = useCallback(() => {
        if (isLiveFeed === "EU" && GB_CURRENCIES.includes(stock?.currency)) {
            websocket.send(JSON.stringify({ type: "last_trades_history_w_ticker", identifier: GBPIdentifier }));
            websocket.send(JSON.stringify({ type: "last_trades_history_w_ticker", identifier: GBXIdentifier }));

            websocket.send(JSON.stringify({ type: "subscribe", identifier: GBPIdentifier }));
            websocket.send(JSON.stringify({ type: "subscribe", identifier: GBXIdentifier }));
        } else if (isLiveFeed === "NA" || isLiveFeed === "EU") {
            websocket.send(JSON.stringify(tsMessage));
            websocket.send(JSON.stringify(priceMessage));
        }
    }, [stock, isLiveFeed, websocket, isGBCurrency]);

    const updateData = useCallback(
        ({ data }: MessageEvent) => {
            const { ISIN, LT, T, "ts-last": tsLast, ticker } = JSON.parse(data);
            const identifiers = [stock.ticker, stock.isin + stock.currency, GBPIdentifier, GBXIdentifier];
            if (T?.i && identifiers.includes(T.i)) {
                const newPrice = T.C.c;

                if (!price || Math.abs(parseFloat(price) - newPrice) > 0.01) {
                    setPrice(newPrice);
                }
            } else if (tsLast && ISIN === stock.isin) {
                const newPrice = tsLast[1];

                if (!price || Math.abs(parseFloat(price) - newPrice) > 0.01) {
                    setPrice(newPrice);
                }
            } else if (LT?.[LT.length - 1] && identifiers.includes(ticker)) {
                const newPrice = LT[LT.length - 1][0].c;

                if (!price || Math.abs(parseFloat(price) - newPrice) > 0.01) {
                    setPrice(newPrice);
                }
            }
        },
        [stock, websocket, isGBCurrency]
    );

    useEffect(() => {
        setPrice(undefined);

        if (isLiveFeed && websocket) {
            websocket.readyState === 1 ? sendMessage() : websocket.addEventListener("open", sendMessage);
            websocket.addEventListener("message", updateData);
        }

        return () => {
            if (isLiveFeed && websocket) {
                websocket.removeEventListener("open", sendMessage);
                websocket.removeEventListener("message", updateData);
                if (websocket.readyState === WebSocket.OPEN && !isStockInTickerTape) {
                    if (isGBCurrency) {
                        websocket.send(JSON.stringify({ ...priceMessage, type: "unsubscribe", identifier: GBPIdentifier }));
                        websocket.send(JSON.stringify({ ...priceMessage, type: "unsubscribe", identifier: GBXIdentifier }));
                    } else {
                        websocket.send(JSON.stringify({ ...priceMessage, type: "unsubscribe", identifier: priceMessage.identifier }));
                    }
                }
            }
        };
    }, [stock.stock_id, isLiveFeed, websocket, isStockInTickerTape]);

    useEffect(() => {
        setIsStockInTickerTape(!!tickerStocks[`${stock.exchangeSymbol}:${stock.ticker}`]);
    }, [tickerStocks, stock]);

    return (
        <div className={styles.StockLinksStock}>
            {price ? (
                <div className={styles.StockLinkInfo}>
                    <span className={classNames(styles.StockLinkInfoHeader, percentageDifference < 0 ? "negative" : "positive")}>{formatPriceWithPrecision(parseFloat(price))}</span>
                    <span className={classNames(styles.StockLinkInfoSubHeader, percentageDifference < 0 ? "negative" : "positive")}>
                        {isFinite(closeDifference) ? sign + formatPriceWithPrecision(closeDifference) : "N/A"} {isFinite(percentageDifference) ? `(${percentageDifference.toFixed(2)}%)` : "(N/A%)"}
                    </span>
                </div>
            ) : !websocket && stock.last_close ? (
                <div className={styles.StockLinkInfo}>
                    <span className={styles.StockLinkInfoHeader}>{formatPriceWithPrecision(stock.last_close)}</span>
                    <span className={styles.StockLinkInfoTag}>Last close</span>
                </div>
            ) : null}

            <StockHeaderSession stock={stock} />
        </div>
    );
};

export default StockHeaderInfo;
